import { ProductKey } from '@/components/signup/utils/products';

import { PRODUCT_KEY_MAP } from '../util/constants';
import { XOR } from '../util/types';

export type PermissionLevel = 'ADMIN' | 'NON_ADMIN';

export type Cookie = {
	readonly name: string;
	readonly value: string;
};

export type MeOptions = {
	readonly cloudSessionTokenCookie?: Cookie;
};

export type CloudSessionTokenCookieType = {
	readonly cloudSessionTokenCookie?: Cookie;
};

export type AisAvailableProductsOptions = {
	readonly includeInvalidSites?: boolean;
	readonly includeInvalidBitbucketSite?: boolean;
	readonly removeNonAdminSites?: boolean;
	readonly makeS2sRequest?: boolean | undefined;
	readonly userContext?: string | undefined;
	readonly retryAttempted?: boolean | undefined;
} & CloudSessionTokenCookieType;

export type GetAvailableSitesResponse = {
	readonly sites: Site[];
	readonly flags: SiteFlags;
	readonly containsProvisionableSiteEntitlement: boolean;
};

export type GetAvailableSitesRequest = {
	readonly orderId: string;
	readonly transactionAccount: string;
};

export type GetAccountOrgsRequest = {
	readonly skipNames?: boolean;
	readonly getOrgType?: boolean;
	readonly includeDefaultDir?: boolean;
} & CloudSessionTokenCookieType;
export type GetAccountOrgsResponse = {
	readonly success: boolean;
	readonly accountOrgs?: {
		id: string;
		name: string;
		isAdmin: boolean;
		defaultDir?: boolean;
	}[];
};

/**
 * A Unit (isolation container) within an Org, sourced from the Atlassian GraphQL
 * gateway `admin_unitsForOrg` query. Used by the WAC Bitbucket unit picker so admins
 * of multi-unit orgs can choose where the workspace lives (COMMIT-29289).
 */
export type BitbucketOrgUnit = {
	readonly id: string;
	readonly name: string;
};
export type GetBitbucketOrgUnitsRequest = {
	readonly orgId: string;
	readonly first?: number;
} & CloudSessionTokenCookieType;
export type GetBitbucketOrgUnitSettingsRequest = {
	readonly orgId: string;
} & CloudSessionTokenCookieType;

export type GetAdminhubAccountOrgsRequest = CloudSessionTokenCookieType;

export type GetAdminhubAccountOrgsResponse = {
	readonly success: boolean;
	accountOrgs?: {
		readonly id: string;
		readonly name: string;
	}[];
};

export type GetAdminhubOrgDetailsRequest = {
	readonly orgId: string;
} & CloudSessionTokenCookieType;
export type GetAdminhubOrgDetailsResponseDto = {
	id: string;
	name: string;
	rovoEnabled?: boolean | undefined;
	accessEnabled?: boolean | undefined;
	sites?: unknown[] | undefined;
};
export type GetAdminhubOrgDetailsResponse = {
	readonly success: boolean;
	readonly organization?: GetAdminhubOrgDetailsResponseDto;
};

export type UseAdminhubOrgDetailsRequest = {
	pageSize?: number;
} & GetAccountOrgsRequest;
export type UseAdminhubOrgDetailsResponse = {
	readonly success: boolean;
	readonly accountOrgs: GetAccountOrgsResponse;
	readonly orgDetails: GetAdminhubOrgDetailsResponse[];
};

export type GetSignupAccountOrgDetailsRequest = {
	readonly orgId: string;
	readonly offeringIds?: string[];
} & CloudSessionTokenCookieType;
export type GetSignupAccountOrgDetailsResponseDto = {
	id: string;
	displayName: string;
	siteIds?: string[];
	entitlementsByOfferingIds?: {
		offeringId: string;
		entitlementIds: string[];
	}[];
};
export type GetSignupAccountOrgDetailsResponse = {
	readonly success: boolean;
	readonly organization?: GetSignupAccountOrgDetailsResponseDto;
};

export type UseSignupAccountOrgDetailsRequest = {
	offeringIds?: string[];
	pageSize?: number;
	preselectedOrgId?: string;
} & GetAccountOrgsRequest;
export type UseSignupAccountOrgDetailsResponse = {
	readonly success: boolean;
	readonly accountOrgs: GetAccountOrgsResponse;
	readonly orgDetails: GetSignupAccountOrgDetailsResponse[];
};

export type GetAccountProductsRequest = CloudSessionTokenCookieType;
export type GetAccountProductsResponse = {
	readonly success: boolean;
	readonly accountProducts?: {
		displayName: string;
		cloudId: string;
		products: string[];
		productsAndEditions: { productKey: string; productEdition: string }[];
	}[];
};

export type Site = {
	readonly siteName: string;
	readonly cloudId: string;
	readonly products: string[];
	readonly displayName: string;
	readonly url: string;
	readonly edition: string;
	readonly entitlements: {
		ccpEntitlementId: string;
		billingSourceSystem: string;
		productKey: string;
	}[];
	readonly adminAccess: boolean;
	readonly orderContext?: {
		readonly orderId: string;
		readonly slug?: string;
		readonly conflictingProducts?: string[];
		readonly conflictingEntitlementIds?: string[];
	};
};

export type SiteFlags = {
	readonly useLockedInSite: boolean;
	readonly lockedInSiteName?: string;
	readonly provisionableSiteOfferings?: {
		offeringId: string;
		productKey?: string;
		licenseDataSource?: string;
		edition?: string;
	}[];
	readonly productConflictingSites?: Site[];
};

export type AvailableProductsType = {
	readonly adminAccess: boolean;
	readonly cloudId: string;
	readonly displayName: string;
	readonly url: string;
	readonly availableProducts: [
		{
			readonly activityCount: number;
			readonly productType: keyof typeof PRODUCT_KEY_MAP;
			readonly url?: string;
		},
	];
	readonly avatar?: string;
};

export type AisSitesType = {
	readonly adminAccess: boolean;
	readonly cloudId: string;
	readonly products: string[];
	readonly url: string;
	readonly displayName: string;
	readonly edition?: string | null;
	readonly orgId?: string | null;
};
export type AvailableProductsResponse = {
	sites: AisSitesType[];
	isNewUser: boolean;
	callFailed?: true;
};

export type AvailableProductsLegacyResponse = SitesType[];

export type AvailableProductsOptions = {
	readonly includeInvalidSites?: boolean;
	readonly includeInvalidBitbucketSite?: boolean;
	readonly removeNonAdminSites?: boolean;
} & CloudSessionTokenCookieType;

export enum AuthenticationMethodsType {
	PASSWORD = 'password',
	GOOGLE = 'google',
	SAML = 'saml',
	MICROSOFT = 'microsoft',
	APPLE = 'apple',
	SLACK = 'slack',
}

export enum SampleDataVersion {
	MATURE = 'MATURE',
	WIZARD = 'WIZARD',
	GENERIC = 'GENERIC',
	MATUREV2 = 'MATUREV2',
	SPACE_ONBOARDING = 'SPACE_ONBOARDING',
}

export type SampleDataVersionType =
	| 'MATURE'
	| 'WIZARD'
	| 'GENERIC'
	| 'GENERICV2'
	| 'MANUAL'
	| 'MATUREV2'
	| 'SPACE_ONBOARDING';

export type BitbucketWorkspaceOptions = {
	readonly nextUrl?: string;
	readonly urlQuery?: string;
} & CloudSessionTokenCookieType;

export type BitbucketWorkspacesResponse = {
	readonly page: number;
	readonly pagelen: number;
	readonly size: number;
	readonly next?: string;
	readonly previous?: string;
	readonly values: [
		{
			// owner: has admin access, collaborator: has write access
			readonly permission: 'owner' | 'member' | 'collaborator';
			readonly user?: WorkspaceUserType;
			readonly workspace?: WorkspaceType;
			readonly last_accessed?: string | null;
			readonly type?: string;
			readonly links?: Record<string, unknown>;
			readonly added_on?: string | null;
		},
	];
};

export type WorkspaceUserType = {
	readonly display_name: string;
	readonly account_id: string;
	readonly links: Record<string, unknown>;
	readonly nickname: string;
	readonly type: string;
	readonly uuid: string;
};

export type WorkspaceType = {
	readonly name: string;
	readonly type: string;
	readonly uuid: string;
	readonly links: Record<string, unknown>;
	readonly slug: string;
};

export type CheckAuthenticationMethodsAvailableRequest = {
	readonly email: string;
	readonly application?: string;
	readonly successRedirect: string;
};
type Method = {
	readonly type: AuthenticationMethodsType;
	readonly redirect_to?: string;
};

export type CheckAuthenticationMethodsAvailableResponse = {
	readonly methods: Method[];
	readonly userExists: boolean;
	readonly passwordEnabled: boolean;
	readonly samlRequired?: boolean;
};

export type MeResponse = {
	readonly account_id: string;
	readonly name: string;
	readonly email: string;
	readonly picture: string;
	readonly locale: string;
	readonly zoneinfo: string;
};

export type UserProfile = {
	readonly accountId: string;
	readonly name: string;
	readonly email: string;
	readonly picture: string;
	readonly locale: string;
	readonly zoneinfo: string;
};

export type UserProfileOptions = {
	readonly cloudSessionTokenCookie?: Cookie;
	readonly productName?: string;
	readonly userContext?: string;
	readonly makeS2sRequest?: boolean | undefined;
};

export interface CompanySite {
	accessType: AccessRequestCapability;
	cloudId: string;
	displayName: string;
	productName: string;
	productKey: string;
	productEdition: string;
	productSeatCount: number;
	productRoleAri: string;
	productResourceAri: string;
	productUrl: string;
	avatarUrl: string;
}

export type ExistingSite = {
	cloudId: string;
	displayName: string;
	url: string;
	hasAdminAccess: boolean;
	siteCreatedAt: number;
	products: {
		productName: string;
		productKey: string;
		productEdition: string;
		billingSourceSystem?: string;
		collectionEntitlementId?: string;
		entitlementId?: string;
		partOfCollection?: string;
	}[];
};

export type UserProfileResponse = {
	readonly cohorts: {
		userType: 'new-user' | 'existing-user';
		domainType: 'business-domain' | 'personal-domain';
		expandType: 'contact-expand' | 'company-expand' | 'not-implemented';
		companyType: 'existing-company' | 'new-company' | 'not-applicable' | 'unknown';
	};
	readonly attributes: {
		isProvisioniningInProgress: boolean;
		managingOrg?: {
			orgId: string;
			cloudIds?: string[];
			offeringIds?: string[];
		};
		existingProductsFromOrg: CompanySite[];
		existingSites: ExistingSite[];
		hasExistingSites: boolean | undefined;
		hasExistingProductsFromOrg: boolean | undefined;
		hasAccountManagedByOrg: boolean | undefined;
		orgProductContext: string | undefined;
		orgProductDirectAccessCount: number | undefined;
		orgProductRequestAccessCount: number | undefined;
		orgProductMaxCapacityCount: number | undefined;
	};
};

export type CcpProduct = {
	readonly offeringId: string;
	readonly pricingPlanId: string;
	readonly productKey: string;
	readonly edition: string;
	readonly chargeElement: string;
	readonly pricingType?: string;
	readonly childProductsForPriorityActivation?: string[];
};
export type Platform = 'ccp' | 'hams' | 'routing';
export type Consent = {
	readonly site: string | null;
	readonly locale: string | null;
	readonly formUrl: string | null;
	readonly consents: ConsentsObject[];
};
type ConsentsObject = {
	key: string;
	displayedText: unknown;
	granted: boolean;
};

export type ActivateDirectBuyOnOrgRequest = {
	timezone: string;
	region: string;
	orderId: string;
	transactionAccountId: string;
	orgId: string;
} & CloudSessionTokenCookieType;

export type ActivateDirectBuyOnOrgResponse = { provisioningRequestId: string };

export type ActivateProductRequest = {
	email: string;
	ccpProducts: CcpProduct[];
	siteName?: string;
	transactionAccountId?: string;
	timezone: string;
	useCcpPlatform: boolean;
	platform: Platform;
	displayName?: string;
	consent?: Consent;
	signupContext?: string;
	advancedSettings?: {
		[key: string]: number | boolean | string | Record<string, unknown>;
	};
	gRecaptchaResponse?: string;
	testingPurposes?: boolean;
	isNewUser?: boolean;
	migrationId?: string;
	signupId?: string;
	/**
	 * Collaboration-context (Unit) id the Bitbucket workspace should be provisioned into.
	 * Set only when an admin of a multi-unit org selects a Unit; the Signup server maps
	 * this to the BXP `collabContextId`. Omitted for single-unit orgs (server derives it
	 * from the org's cc-default-directory).
	 */
	collabContextId?: string;
} & CloudSessionTokenCookieType;

export type ActivateProductUpstreamResponse = XOR<
	ActivateProductCcpUpstreamResponse,
	ActivateProductCofsResponse
>;
type ActivateProductResponseItems = {
	itemId: string;
	orderItemId: string;
};
export type ActivateProductCcpUpstreamResponse = {
	statusUrl: string;
	orderId: string;
	slug: string;
	items: ActivateProductResponseItems[];
	transactionAccountId: string;
	invoiceGroupId: string;
	metadata: {
		provisionRequestId: string;
	};
	cloudId: string;
	service: string;
};

export type ActivateProductResponse = XOR<ActivateProductCcpResponse, ActivateProductCofsResponse>;

export type ActivateProductCcpResponse = {
	orderId: string;
	slug: string;
	transactionAccountId: string;
	invoiceGroupId: string;
	metadata: {
		provisionRequestId: string;
		products?: Array<{ edition?: string }>;
	};
	items: ActivateProductResponseItems[];
	cloudId: string;
	service: string;
	orchestrationId?: string;
};

export type ActivateProductCofsResponse = {
	cloudId: string;
	progressUri: string;
	service: string;
	orchestrationId?: string;
};

export type CheckCloudNameAvailabilityRequest = {
	readonly cloudName: string;
} & CloudSessionTokenCookieType;
export type CloudNameAvailability =
	| 'AVAILABLE'
	| 'TAKEN'
	| 'ERROR'
	| 'INVALID'
	| 'NOT_AVAILABLE_FOR_TRANSFER';
export type CheckCloudNameAvailabilityResponse = {
	readonly availability: CloudNameAvailability;
};

export type GetRecommendedSiteNameRequest = {
	readonly email: string;
} & CloudSessionTokenCookieType;

export type GetRecommendedSiteNameResponse = {
	readonly recommendedName: string;
};

export type GetUserPermissionsIsPermittedRequest = {
	readonly permissionId: GetUserPermissionsIsPermittedRequestPermissionId;
	readonly resourceId: string;
	readonly dontRequirePrincipalInSite?: boolean;
};

export type GetUserPermissionsIsPermittedResponse = {
	readonly permitted: boolean;
};

export enum GetUserPermissionsIsPermittedRequestPermissionId {
	ADD_PRODUCTS = 'add-products',
	INVITE_USERS = 'invite-users',
	MANAGE = 'manage',
	WRITE = 'write',
	/**
	 * @deprecated ADD_PRODUCT is a legacy permissionId, use ADD_PRODUCTS instead.
	 */
	ADD_PRODUCT = 'addProduct',
}

export type PostExpandFreeValidationRequest = {
	readonly cloudId: string;
	readonly productKeys: string[];
};

export type PostExpandFreeValidationResponse = {
	readonly isInBreachOfFree: boolean;
};

export type GetProductEditionRequest = {
	readonly cloudId: string;
	readonly productKey: string;
};

export type GetActivationAuthorizationRequest = {
	readonly productKeys: string[];
	readonly productEdition: string;
	readonly cloudId?: string;
	readonly siteName: string;
};

export type GetActivationAuthorizationResponse = {
	readonly redirectUrl: string;
	readonly authorized: boolean;
};

export type GetCcpHamsStatus = {
	readonly cloudId: string;
	readonly offeringKeys: string;
	readonly siteName?: string;
};
export type GetCcpHamsResponse = {
	readonly service: string;
};

export interface GetLicenceInformationRequest {
	cloudId: string;
}

export type GetLicenseInformationResponse = {
	readonly firstActivationDate: number;
	readonly hostname: string;
	readonly products: {
		[key: string]: GetLicenseInformationProduct;
	};
};

export type GetLicenseInformationProduct = {
	readonly ccpEntitlementId?: string;
	readonly ccpTransactionAccountId: string;
	readonly edition: string;
	readonly isSuspended?: boolean;
	readonly state: string;
	readonly relatesFromEntitlements?: GetLicenseInformationRelatedEntitlement[];
};

export type GetLicenseInformationRelatedEntitlement = {
	readonly relationshipType: string;
	readonly entitlementId: string;
	readonly relationshipId: string;
};

export interface GetUserSegmentationRedirectAdviceRequest {
	readonly userId: string;
	readonly continue: string;
}

export interface GetUserSegmentationRedirectAdviceResponse {
	readonly redirect: boolean;
	readonly cloudId: string;
	readonly duration: number;
}

export interface PostRequestAccessUserResourceRequest {
	readonly cloudSessionTokenCookie?: Cookie;
	readonly requestBody: {
		accountId?: string;
		resourceAri: string;
		productName: string;
		accessType: AccessRequestCapability;
		roleAri: string;
	};
}

export enum AccessRequestCapability {
	DIRECT_ACCESS = 'DIRECT_ACCESS',
	REQUEST_ACCESS = 'REQUEST_ACCESS',
	ACCESS_GRANTED = 'ACCESS_GRANTED',
	ACCESS_EXISTS = 'ACCESS_EXISTS',
	PENDING_REQUEST_CREATED = 'PENDING_REQUEST_CREATED',
	PENDING_REQUEST_EXISTS = 'PENDING_REQUEST_EXISTS',
	APPROVED_REQUEST_EXISTS = 'APPROVED_REQUEST_EXISTS',
	DENIED_REQUEST_EXISTS = 'DENIED_REQUEST_EXISTS',
	FORBIDDEN = 'FORBIDDEN',
	VERIFICATION_REQUIRED = 'VERIFICATION_REQUIRED',
	ERROR = 'ERROR',
}

export enum AccessRole {
	PRODUCT_MEMBER = 'product/member',
	PRODUCT_HELPSEEKER = 'product/helpseeker',
}

export interface PostRequestAccessUserResourceResponse {
	readonly accessStatus: AccessRequestCapability;
	readonly role: AccessRole;
}

export interface ProductActivateRequest {
	readonly transactionAccountId: string;
	readonly entitlementId: string;
	readonly offerings: OfferingsType[];
	readonly cloudId: string;
	readonly siteName?: string;
	readonly advancedSettings?: advancedSettingsType;
}

export interface ProductActivationStatusRequest {
	provisioningRequestId: string;
	provisionPollingTimes?: number;
}

export interface ProductActivationStatusResponse {
	provisionRequestId: string;
	resourcePackages: ResourcePackage[];
	twcOnboardingStatus?: {
		status: string;
		onboardingUrl?: string;
	};
}

export type UpdateIdentityProfileParams = {
	readonly aaid: string;
	readonly extendedProfile: ExtendedProfileType;
};

export type ExtendedProfileType = {
	readonly job_title?: string;
	readonly organization?: string;
	readonly department?: string;
	readonly location?: string;
	readonly team_type?: string;
	readonly phone_number?: string;
};

export interface ResourceActivationError {
	source: string;
	message: string;
}

export interface ResourcePackage {
	entitlementId: string;
	action: {
		type: 'ACTIVATE';
		status: 'IN_PROGRESS' | 'SUCCESS' | 'PROVISIONING_FAILURE';
		errorDetails?: ResourceActivationError[];
	};
	resourceAris: string[];
}

export type GetCloudStatusOptions = {
	readonly cloudId: string;
	readonly requestId: string;
};

export type GetCloudStatusResponse = {
	readonly cloudId: string;
	readonly requestId: string;
	readonly completed: boolean;
	readonly failureReason: Readonly<Record<string, string>>;
	readonly requestType: string;
	readonly successful: boolean;
	readonly billingDetails: unknown;
	readonly bestStatuses: Readonly<Record<string, string>>;
};

// For cross flow /product/activate endpoint
export type advancedSettingsType = {
	readonly signupContext?: string;
};
export type OfferingsType = {
	readonly offeringId: string;
	readonly pricingPlanId: string;
	readonly chargeElement: string;
};
export type ProductActivateResponse = {
	readonly orderId: string;
	readonly progressUri: string;
	readonly cloudId: string;
};

export type PostCloudSignUpRequest = {
	payload: FormPayloadType | FormPayloadActivateProducts;
};

export interface COFSProductActivateRequest {
	readonly edition: string;
	readonly productKey: string;
	readonly cloudId: string;
}

export type COFSProductActivateResponse = {
	readonly progressUri: string;
	readonly cloudId: string;
};

type FormPayloadType = {
	adminUsername: string;
	gRecaptchaResponse?: string;
	cloudName: string | null;
	email: string | null;
	timezone: string | null;
	firstName: string | null;
	lastName: string | null;
	adminPassword?: string | null;
	signupContext?: string;
	dataRegion?: string;
	consent: Consent;
} & FormPayloadActivateProducts;

type FormPayloadActivateProducts = {
	activateProducts?: boolean;
	products: ProductPayloadObject[];
	cloudId?: string;
	cloudName: string | null;
	advancedSettings?: {
		[key: string]: number | boolean | string | Record<string, unknown>;
	};
	signupContext?: string;
};

type ProductPayloadObject = {
	readonly accountName?: string | null;
	readonly edition?: string | null;
	readonly product?: string | null;
	readonly productKey?: string | null;
};

export type ProductType = {
	productKey?: string;
	edition?: string;
};

export type UserSignupRequest = {
	email: string;
	locale: string;
	redirectTo: string;
	application?: string;
	displayName: string;
	gRecaptchaResponse?: string;
	products?: ProductType[];
};

export type UserSignupResponse = {
	id: string;
	otpRedirect?: string;
};

export type LocaleType = {
	locale?: string;
	localeRequiresMarketingCommunicationOptIn?: boolean;
	usersChoiceOnMarketingCommunicationOptIn?: boolean;
	hasUserMadeChoiceOnMarketingCommunicationOptIn?: boolean;
};

export type UpdateConsentPreferencesRequest = {
	formId: string;
	site?: string;
	source?: string;
	email: string;
	locale?: string;
	paramsLocale?: string;
	formUrl: string;
	consentLabelText?: string;
	consentCheckboxValue: boolean;
	privacyPolicyText?: string;
	ndaText?: string;
};

export type CreateSpaceParams = {
	description: string;
	id: string;
	key: string;
	name: string;
	url: string;
};

export type SubmitConfluenceOnboardingSelectionsRequest = {
	teamType: string;
	teamSpaceName: string;
	contentType: string;
	siteName: string;
	userDisplayName: string;
	inviteUserEmails: string[];
	userId: string;
	cloudId: string;
	domain: string;
};

export type SubmitConfluenceOnboardingStepStatus = {
	succeeded: boolean;
	message: string;
};

export type LandOnPath = {
	succeeded: boolean;
	message: string;
	landOnPathNBPS: string;
	improvedGetStartedWhiteboardPath: string;
	improvedGetStartedPath: string;
};

export type SubmitConfluenceOnboardingSelectionsResponse = {
	personalSpace: SubmitConfluenceOnboardingStepStatus;
	teamSpace: SubmitConfluenceOnboardingStepStatus;
	emailInvites: SubmitConfluenceOnboardingStepStatus;
	updateProfile: SubmitConfluenceOnboardingStepStatus;
	skipOnboarding: SubmitConfluenceOnboardingStepStatus;
	teamSpacePath: string;
	landOnPath: LandOnPath;
};

export type GetInviteLinkRequest = {
	siteName: string;
	cloudId: string;
};

export type GetInviteLinkResponse = {
	invitationUrl: string;
};

export interface ProductActivationStatusRequest {
	provisioningRequestId: string;
}

export interface ResourcePackage {
	entitlementId: string;
	action: {
		type: RecourcePackageActionTypes;
		status: ProductActivationStatus;
		errorDetails?: ResourceActivationError[];
	};
	resourceAris: string[];
}

export type RecourcePackageActionTypes = 'ACTIVATE';

export type ProductActivationStatus = 'IN_PROGRESS' | 'SUCCESS' | 'PROVISIONING_FAILURE';

export interface ResourceActivationError {
	source: string;
	message: string;
}

export type SitesType = {
	readonly adminAccess: boolean;
	readonly cloudId: string;
	readonly products: string[];
	readonly url: string;
	readonly displayName: string;
	readonly edition?: string | null;
	readonly orgId?: string | null;
	readonly productsAndEditions?: ProductsAndEditionsType[];
};

type ProductsAndEditionsType = {
	lastActiveTimestamp?: string;
	productEdition: string;
	productKey: string;
};

export type UserSite = {
	products: {
		productKey: string;
		productEdition: string;
	}[];
	cloudId: string;
	displayName: string;
	url: string;
	hasAdminAccess: boolean;
};

type NewUser = {
	type: 'new';
	attributes?: {
		inProgress?: boolean;
	};
};

type ExistingUser = {
	type: 'existing';
	attributes: {
		sites: UserSite[];
		inProgress?: boolean;
	};
};

export type User = {
	consumer?: NewUser | ExistingUser;
};

export type JiraProjectCreateRequest = {
	readonly key: string;
	readonly templateKey: string;
	readonly name: string;
	readonly siteName: string;
};

export type JiraSimplifiedProjectCreateRequest = {
	readonly cloudId: string;
	readonly key: string;
	readonly templateKey: string;
	readonly name: string;
};

export type GetJiraSimplifiedProjectTemplateKeysRequest = {
	readonly cloudId: string;
};

export type GetJiraProjectByKeyRequest = {
	readonly cloudId: string;
	readonly projectKey: string;
};

export type JiraProjectSummary = {
	readonly projectId: number;
	readonly projectKey: string;
	readonly projectName: string;
	readonly simplified: boolean;
};

export type JiraProjectCreateResponse = {
	readonly returnUrl: string;
	readonly projectId: number;
	readonly projectKey: string;
	readonly projectName: string;
	readonly simplified: boolean;
};

export enum JiraProjectCreateErrors {
	noSiteAccess = 'noSiteAccess',
	badRequest = 'badRequest',
	badGateway = 'badGateway',
	projectNameCollision = 'projectNamingCollision',
	projectKeyCollision = 'projectKeyCollision',
	noProjectCreateAccess = 'noProjectCreateAccess',
}

export type GetActivationRequest = {
	readonly orderId: string;
	readonly transactionAccount: string;
};

export type OrderItem = {
	offeringId: string;
	entitlementId: string;
	metadata?: {
		orgId?: string;
	};
};

export type GetActivationResponse = {
	activationType: 'ORG' | 'SITE';
	orderItems: OrderItem[];
	orgBasedSiteOrderItems?: OrderItem[];
};

export type CreateOrgRequest = {
	readonly orgDisplayName: string;
	readonly webPlatform?: string;
};

export type CreateOrgResponse = {
	readonly orgId: string;
	readonly progressUri: string;
};

export type BitbucketValidateWorkspaceSlugRequest = {
	readonly workspaceSlug: string;
};

export type BitbucketValidateWorkspaceSlugResponse = {
	readonly new_ari: string;
};

export interface GetCloudProductInfoRequest {
	cloudId: string;
	webPlatform?: string;
}

export type CloudProductInfo = Record<string, string>;

export type ServerSideRequestStatus = {
	/**
	 * Whether the request should be made or not determined on the server side.
	 */
	requested: boolean;
	/**
	 * `true` if the request was successful
	 * `false` is the request failed and `requested` is true
	 * `false` if the request was not made and `requested` is false
	 */
	success: boolean;
};

export interface ActivateDirectBuyProductForExistingSiteRequest {
	cloudId: string;
}

export interface ActivateDirectBuyProductForNewSiteRequest {
	name: string;
}
export interface ActivateDirectBuyProductRequest {
	timezone: string;
	region: string;
	orderId: string;
	transactionAccountId: string;
	webPlatform?: string;
	orgId?: string;
	site: XOR<
		ActivateDirectBuyProductForExistingSiteRequest,
		ActivateDirectBuyProductForNewSiteRequest
	>;
}

export interface ActivateDirectBuyProductResponse {
	provisionRequestId: string;
}

export const POLL_TASK_TIMEOUT = 45_000;

export type TaskStatus =
	| 'ENQUEUED'
	| 'RUNNING'
	| 'COMPLETE'
	| 'FAILED'
	| 'CANCEL_REQUESTED'
	| 'CANCELLED'
	| 'DEAD';

export interface FetchTaskResponse {
	status: TaskStatus;
	message: string;
	result: {
		projectId: string;
	};
}

export interface FetchProjectRolesResponse {
	[key: string]: string;
}

export class TaskError extends Error {
	public status: string;
	public id: string;
	public taskMessage: string;

	public constructor(
		message: string,
		{ status, taskMessage, id }: { status: string; taskMessage: string; id: string },
	) {
		super(message);
		this.status = status;
		this.id = id;
		this.taskMessage = taskMessage;
	}
}

export type ProjectIssueType = {
	name: string;
	avatarId: number;
};

export type CustomDetailType = {
	enableExtraFields?: boolean;
	sampleDataVersion?: SampleDataVersionType;
	enableCardCover?: boolean;
};

export interface WizardCreateProjectPayload {
	projectKey: string;
	template: string;
	cloudId: string;
	issueTypes: ProjectIssueType[];
	statusNames: string[];
	projectName: string;
	teamType?: string;
	selectedAgentTools?: string[];
	customDetail?: CustomDetailType;
	sampleIssues?: WizardCreateProjectSampleIssue[];
}

export type WizardCreateProjectSampleIssue = {
	refId: string;
	parentRefId?: string;
	issueType: 'Epic' | 'Task';
	transitionId: number;
	relativeDays: number;
	summary: string;
	description?: string;
	priorityId: number;
	assigneeId?: string;
};

export interface WizardCreateProjectResponse {
	taskId: string;
}

type AdvancedSettings = {
	readonly suppressEmail?: boolean;
	readonly initialProductURL?: string;
	readonly additionalProvisioningData?: {
		readonly workspaceUuid?: string;
		readonly signupUrl?: string;
		readonly signupAction?: string;
		readonly signupLocation?: string;
		readonly dataRegion?: string;
		readonly spProduct?: string;
		readonly signupDevops?: boolean;
		readonly hideUserSegmentation?: boolean;
		readonly orgId?: string;
		readonly confluenceRealm?: string;
		readonly jiraRealm?: string;
	};
	readonly completionCallbackAudience?: string;
	readonly completionCallbackURL?: string;
	readonly originatorIP?: string;
};

export type AsyncNewSiteActivationRequest = {
	advancedSettings?: AdvancedSettings;
	consent?: Consent;
	displayName: string;
	email: string;
	gRecaptchaResponse: string;
	offeringPromotions?: { [key: string]: string };
	signupContext?: string;
	signupProducts: CcpProduct[];
	siteName: string;
	timezone?: string;
	migrationId?: string;
	orgId?: string;
	signupId?: string;
	siteId?: string;
	transactionAccountId?: string;
	workspaceId?: string;
	platform?: string;
	isNewUser?: boolean;
};

export type AsyncActivationStatusRequest = {
	workflowId: string;
};

export type AsyncActivationStatusMetadata = {
	commercePlatform?: string;
	orchestrationId?: string;
	provisioningRequestId?: string;
	provisioningRequestTimestamp?: string;
};

export type AsyncActivationStatusResponse = {
	workflowName: string;
	workflowId: string;
	workflowStatus:
		| 'PROVISIONING_SUCCEEDED'
		| 'RECOVERABLE_ERROR'
		| 'UNRECOVERABLE_ERROR'
		| 'PENDING';
	stepName: string;
	stepId: string;
	stepStatus: 'active' | 'completed' | 'delayed' | 'failed';
	stepFailureReason?: string;
	stepStartedAt?: string;
	metadata?: AsyncActivationStatusMetadata;
};

export interface CreateSampleProjectPayload {
	cloudId: string;
	template: string;
	teamType: string;
	projectKey?: string;
	issueTypes: ProjectIssueType[];
	statusNames: string[];
	projectName?: string;
	customDetail: {
		sampleDataVersion: SampleDataVersion;
		enableExtraFields?: boolean;
	};
}

export type ThemePreferencePayload = {
	cloudId: string;
	product: 'jira' | 'confluence';
	theme: 'light' | 'dark' | 'auto';
};

export interface CreateSampleProjectResponse {
	projectId: string;
}

export type PutSampleProjectUserPropertiesPayload = {
	projectId: string;
	tenantId: string;
	template: string;
};

export type GetOrgServersPayload = {
	orgId: string;
} & CloudSessionTokenCookieType;

export type GetOrgServersResponse = {
	data: {
		serverId: string;
		cloudId: string;
		supportEntitlementNumber: string;
		productKey: string;
		productVersion: string;
		cmaVersion: string;
		organizationName: string;
		nickname: string;
		serverBaseUrl: string;
		userCount: number;
		licenseDetails: {
			name: string;
			count: number;
		};
		totalAdvancedRoadmapPlansCount?: number;
		dashboardsCount?: number;
		globalTemplateCount?: {
			globalPageTemplateCount: number;
			customSystemTemplateCount: number;
		};
	}[];
	links?: {
		self: string;
		next: string;
	};
};

export type GetMigrationPlanTotalsPayload = {
	orgId: string;
} & CloudSessionTokenCookieType;

export type GetMigrationPlanTotalsResponse = {
	totalPlansCompleted: number;
	totalPlansUnsuccessful: number;
	totalPlansRunning: number;
};

export type GetMigrationPlanTotalsProxyResponse = {
	readonly success: boolean;
	readonly data: GetMigrationPlanTotalsResponse;
};

export type AvailableProduct = {
	productType: string;
	url?: string;
	autoProvisionDate?: string;
	firstActiveDate?: string;
	activationDate?: string;
	endUserActivationDate?: string;
	adminActivationDate?: string;
	isJsmPremium?: boolean;
	isCompanyHubPublishedAndPremium?: boolean;
	companyHubName?: string;
};

export interface Organisation {
	id: string;
	displayName: string;
}

export interface AvailableSite {
	adminAccess: boolean;
	availableProducts: AvailableProduct[];
	avatar?: string | null;
	cloudId?: string;
	displayName: string;
	url: string;
	org?: Organisation;
}

export interface AvailableLink {
	linkType: string;
	url: string;
}

export interface AvailableProductsWACResponse {
	startUrl?: string;
	sites: AvailableSite[];
	links: AvailableLink[];
	isPartial: boolean;
	user?: {
		email: string | undefined;
	};
	assetsAsAnAppEnabled?: boolean;
	companyHubAsAnAppEnabled?: boolean;
	isScasAsAppEnabled?: boolean;
	isTeamsAsAppEnabled?: boolean;
	isGoalsAsAppEnabled?: boolean;
	isHomeRebrandEnabled?: boolean;
	isAnalyticsRebrandEnabled?: boolean;
	isTeam25AppRebrandEnabled?: boolean;
	switcherTeam25ExperimentCohort?: 'control' | 'test' | 'not-enrolled';
}

export type GetL1AssessmentsPayload = {
	orgId: string;
} & CloudSessionTokenCookieType;

export type GetL1AssessmentsProxyResponse = {
	readonly success: boolean;
	readonly data: GetL1AssessmentsResponse;
};

export type L1ProductType = 'JIRA' | 'CONFLUENCE';

export type L1UploadMethod =
	| 'MANUAL'
	| 'AUTOMATIC'
	| 'TRAC'
	| 'MANUAL_TRAC'
	| 'ASSESSMENTS_INTERNAL';

export type L1DeploymentType = 'CLOUD' | 'DATA_CENTER';

export type L1Status =
	| 'NOT_STARTED'
	| 'ASSESSING'
	| 'REASSESSING'
	| 'COMPLETED'
	| 'FAILED'
	| 'NO_ASSESSMENT_DATA';

export type L1InsightsStatus =
	| 'HAS_ERRORS'
	| 'IN_PROGRESS'
	| 'UPDATING'
	| 'UP_TO_DATE'
	| 'NO_DATA'
	| 'PENDING'
	| 'NOT_STARTED';

export type GetL1AssessmentsResponse = {
	id?: string;
	orgId?: string;
	serverId?: string;
	baseUrl?: string;
	products?: {
		sen?: string;
		product?: string;
		productVersion?: string;
	};
	assessmentZoneId?: string;
	assessmentTimeEpochSeconds?: number;
	productType?: L1ProductType;
	objectId?: string;
	uploadMethod?: L1UploadMethod;
	deploymentType?: L1DeploymentType;
	config?: {
		type: string;
		collectBrowserMetrics?: boolean;
		collectUsageMetrics?: boolean;
		executeMacroAssessment?: boolean;
		queryConfig?: {
			exclude?: string[];
		};
		collectUsageMetricsConfig?: {
			instant?: boolean;
		};
	};
	version: number;
	recommendationsCount: number;
	previousRecommendationsCount: number;
	totalResultsCount: number;
	assessmentStatus: L1Status;
	consolidatedInsightsStatus: L1InsightsStatus;
	link: { id: string; status: string };
	instanceId: string;
	sandbox: boolean;
	cloudId: string;
	linkId: string;
	linkStatus: string;
}[];

export type GetCustomerSupportRequestsPayload = {
	request: string;
} & CloudSessionTokenCookieType;

export type GetCustomerSupportRequestsResponse = {
	data: {
		customerSupport: {
			me: {
				requests: {
					total: number;
					page: {
						id: string;
						status: {
							category: string;
							text: string;
						};
						summary: string;
						description: string;
						atlassianCloudUrl: string | null;
						planToMigrate: string | null;
						destinationLicense: string | null;
						sourceLicense: string | null;
						migrationProducts: string | null;
						migrationPhase: string | null;
					}[];
				};
			};
		};
	};
	errors: {
		message: string;
	}[];
};

export type CustomerSupportTicketCommentMutationResponse = {
	data: {
		customerSupport: {
			addComment: {
				message: string;
				author: {
					user: {
						accountId: string;
						name: string;
						picture: string;
					};
				};
				createdDate: {
					dateTime: string;
					friendly: string;
				};
			};
		};
	};
};

export enum SupportRequestStatusCategory {
	Done = 'DONE',
	InProgress = 'IN_PROGRESS',
	Open = 'OPEN',
}

export type GetSupportHierarchicalMigrationRequestResponse = {
	customerSupport?: SupportRequestCatalogQueryApi | null;
};

type SupportRequestCatalogQueryApi = {
	me?: SupportRequestPage | null;
};

type SupportRequestPage = {
	migrationRequests?: {
		total: number;
		page: SupportRequestHierarchyRequest[];
	} | null;
};

export type SupportRequestHierarchyRequest = {
	id: string;
	summary?: string | null;
	requestTypeName: string;
	targetScreen: string;
	reporter: SupportRequestUser;
	status: SupportRequestStatus;
	children?: SupportRequestHierarchyRequest[] | null;
};

export type SupportRequestUser = {
	displayName?: string | null;
	user?: SupportRequestIdentityUser | null;
};

type SupportRequestStatus = {
	category: SupportRequestStatusCategory;
	text: string;
};

type SupportRequestIdentityUser = {
	name: string;
	picture?: string | null;
};

export type GetInstancesPayload = {
	orgId: string;
} & CloudSessionTokenCookieType;

export type GetInstancesResponse = {
	instances: {
		id: string;
		baseUrl: string;
		platform: string;
		productFamily: string;
		serverId: string | null;
		discoveredOrgId: string | null;
		newlyDiscovered: boolean;
		sandbox: string | null;
		discoveredAt: string;
		trustLevel: string;
		plans: [];
		hiddenByAdmin: boolean;
	}[];
	links: {
		prev: string | null;
		next: string | null;
	};
	totalCount: number;
	resultsCount: number;
};

export type GetInstancesProxyResponse = {
	readonly success: boolean;
	readonly data: GetInstancesResponse;
};

export type FastshiftCustomerDomain = {
	readonly customerDomain: string;
	readonly customerName: string;
	readonly customerLogoUrl: string;
	readonly customerDomainId: string;
	readonly hasOver1KPaidSeatsInAtLeastOneProduct: boolean;
};

export type FastshiftTableControlsPayload = {
	page: number;
	size: number;
	sortBy?: string;
	sortDirection?: 'ASC' | 'DESC';
	searchTerm?: string;
	filters: Record<string, string | null>;
};

export type GetFastshiftCustomerDomainsRequest = CloudSessionTokenCookieType &
	FastshiftTableControlsPayload;

export type GetFastshiftUserAccessRequest = CloudSessionTokenCookieType;

export type GetFastshiftUserAccessResponse = {
	readonly data: {
		hasMoveTicketAccess: boolean;
		userType: 'INTERNAL' | 'PARTNER' | 'CUSTOMER';
		userDomain: {
			domainId: string | null;
			domain: string;
			domainName: string;
			logoUrl: string;
		};
	};
};

export type GetFastshiftCustomerDomainsResponse = {
	readonly data: FastshiftCustomerDomain[];
	readonly pagination: {
		readonly page: number;
		readonly size: number;
		readonly totalElements: number;
		readonly totalPages: number;
		readonly hasNext: boolean;
		readonly hasPrevious: boolean;
		readonly sorted: boolean;
	};
};

export type GetFastshiftPortalUserConsentResponse = {
	readonly data: {
		consent: boolean;
	};
	readonly success: boolean;
};

export type GetFastshiftCustomerDomainRequest = CloudSessionTokenCookieType & {
	customerDomainId: string;
};

export type GetFastshiftCustomerDomainResponseData = FastshiftCustomerDomain & {
	readonly relationships: {
		readonly name: string;
		readonly type:
			| 'FAST_SHIFT_CHAMPION'
			| 'SALES_ACCOUNT_EXECUTIVE'
			| 'MIGRATION_SUPPORT_ENGINEER'
			| 'CLOUD_MIGRATION_MANAGER'
			| 'ACCOUNT_EXECUTIVE'
			| 'CUSTOMER_SUCCESS_MANAGER'
			| 'NONE';
		readonly imageUrl: string;
	}[];
	readonly orgsWithAdminRole: {
		readonly orgId: string;
		readonly orgName: string;
	}[];
};

export type GetFastshiftCustomerDomainResponse = {
	readonly data: GetFastshiftCustomerDomainResponseData;
	readonly success: boolean;
};

export type OrgQueryParam = {
	orgId?: string;
	permissionLevel?: PermissionLevel;
};

export type GetCustomerDomainEngagementRequest = CloudSessionTokenCookieType & {
	customerDomainId: string;
};

export type GetFastshiftSupportRequestsRequest = CloudSessionTokenCookieType & {
	customerDomainId: string;
	queryParams: OrgQueryParam;
};

export const FASTSHIFT_TICKET_STATUS = {
	TO_DO: 'TO_DO',
	ON_TRACK: 'ON_TRACK',
	AT_RISK_ATLASSIAN_OWNED: 'AT_RISK_ATLASSIAN_OWNED',
	AT_RISK_PARTNER_OWNED: 'AT_RISK_PARTNER_OWNED',
	NEEDS_CUSTOMER_ATTENTION: 'NEEDS_CUSTOMER_ATTENTION',
	COMPLETE: 'COMPLETE',
} as const;

export type FastshiftTicketStatus =
	(typeof FASTSHIFT_TICKET_STATUS)[keyof typeof FASTSHIFT_TICKET_STATUS];

export const FASTSHIFT_RELATED_TICKET_ISSUE_TYPE = {
	CONSULT: 'CONSULT',
	PLAN: 'PLAN',
	SUPPORT_REQUEST: 'SUPPORT_REQUEST',
	PROBLEM_REPORT: 'PROBLEM_REPORT',
} as const;

export type FastshiftRelatedTicketIssueType =
	(typeof FASTSHIFT_RELATED_TICKET_ISSUE_TYPE)[keyof typeof FASTSHIFT_RELATED_TICKET_ISSUE_TYPE];

export type FastshiftRelatedTicket = {
	associationType: string;
	fastShiftStatusReason: FastshiftTicketStatus;
	issueType: FastshiftRelatedTicketIssueType;
	supportTicketId: string;
	title: string;
};

export type FastshiftSupportRequest = {
	supportTicketId: string;
	title: string;
	status: string;
	fastShiftStatusReason: FastshiftTicketStatus;
	migrationPhase: string;
	source: {
		productName: string | null;
		baseUrl: string | null;
		senId: string;
	};
	destination: {
		productName: string | null;
		baseUrl: string | null;
	};
	planToMigrateDate: string | null;
	relatedTicketsCount: number;
	relatedTickets: FastshiftRelatedTicket[];
};

export type GetFastshiftSupportRequestsResponseData = FastshiftSupportRequest[];

export type GetFastshiftSupportRequestsResponse = {
	readonly data: GetFastshiftSupportRequestsResponseData;
};

export type GetFastshiftOrganizationsRequest = CloudSessionTokenCookieType & {
	domainId: string;
};

export type FastshiftOrganization = {
	orgId: string;
	orgName: string | null;
};

export type GetFastshiftOrganizationsResponseData = FastshiftOrganization[];

export type GetFastshiftOrganizationsResponse = {
	readonly data: GetFastshiftOrganizationsResponseData;
};

export const FastshiftExpectedError = {
	NO_ACCESS: 'NO_ACCESS',
	FORBIDDEN: 'FORBIDDEN',
	NOT_FOUND: 'NOT_FOUND',
} as const;

export type FastshiftExpectedError =
	(typeof FastshiftExpectedError)[keyof typeof FastshiftExpectedError];

export type GetFastshiftOrganizationSummaryRequest = CloudSessionTokenCookieType & {
	domainId: string;
	orgId: string;
};

export type FastshiftOrganizationSummaryResponseData = {
	orgId: string;
	orgName: string | null;
	dataCentersAssessed: number | null;
	areasForImprovement: number | null;
	deletedApps: number | null;
	migrationsCompleted: number | null;
	migrationsInProgress: number | null;
	migrationsUnsuccessful: number | null;
};

export type GetFastshiftOrganizationSummaryResponse = {
	readonly data: FastshiftOrganizationSummaryResponseData;
};

export type GetFastshiftOrganizationDcInstancesRequest = CloudSessionTokenCookieType & {
	domainId: string;
	orgId: string;
};

export type FastshiftDcProductType = 'JIRA' | 'CONFLUENCE' | 'UNKNOWN';

export const FASTSHIFT_DC_CONNECTION_STATUSES = {
	INITIALIZED: 'Initialized',
	REGENERATING: 'Regenerating',
	CONNECTED: 'Connected',
	DISABLED: 'Disabled',
	EXPIRED: 'Expired',
	INTERRUPTED: 'Interrupted',
	FAILED: 'Failed',
	OFFLINE: 'Offline',
} as const;

export type FastshiftDcConnectionStatus =
	(typeof FASTSHIFT_DC_CONNECTION_STATUSES)[keyof typeof FASTSHIFT_DC_CONNECTION_STATUSES];

export const FASTSHIFT_DC_ASSESSMENT_STATUSES = {
	PENDING: 'PENDING',
	IN_PROGRESS: 'IN_PROGRESS',
	UPDATING: 'UPDATING',
	UP_TO_DATE: 'UP_TO_DATE',
	ACTION_REQUIRED: 'ACTION_REQUIRED',
	HAS_ERRORS: 'HAS_ERRORS',
	NO_DATA: 'NO_DATA',
	OUTDATED: 'OUTDATED',
} as const;

export type FastshiftDcAssessmentStatus =
	(typeof FASTSHIFT_DC_ASSESSMENT_STATUSES)[keyof typeof FASTSHIFT_DC_ASSESSMENT_STATUSES];

export type FastshiftOrganizationDcInstance = {
	linkId: string;
	baseUrl: string;
	productType: FastshiftDcProductType | null;
	status: FastshiftDcConnectionStatus;
	lastPingedTime: string | null;
	assessmentStatus: FastshiftDcAssessmentStatus | null;
};

export type GetFastshiftOrganizationDcInstancesResponseData = FastshiftOrganizationDcInstance[];

export type GetFastshiftOrganizationDcInstancesResponse = {
	readonly data: GetFastshiftOrganizationDcInstancesResponseData;
};

export type GetFastshiftDcInstanceDetailsRequest = CloudSessionTokenCookieType & {
	domainId: string;
	orgId: string;
	instanceId: string;
};

export type FastshiftAssessmentRisk = 'LOW_RISK' | 'SKIPPED' | 'MEDIUM_RISK' | 'HIGH_RISK';

export type FastshiftAssessmentImpactType =
	| 'EXTENDS_TIME'
	| 'NEEDS_OPTIMIZATION'
	| 'LIMITS_ENTITIES'
	| 'LIMITS_SYNCING'
	| 'LIMITS_ACCESS'
	| 'WITHIN_GUARDRAILS'
	| 'SKIPPED';

export type FastshiftAssessmentResult = {
	id: string;
	category: string;
	dataPoint: string;
	dataPointSummary?: string;
	customerData: string;
	limit?: string;
	impact: {
		status: FastshiftAssessmentRisk;
		impactType: FastshiftAssessmentImpactType;
		impactText: string;
	};
	hasRecommendations: boolean;
	hasData: boolean;
};

export type FastshiftDcInstanceDetails = {
	instanceId: string;
	baseUrl: string | null;
	productType: FastshiftDcProductType;
	version: string | null;
	serverId: string | null;
	connectionStatus: FastshiftDcConnectionStatus | null;
	assessment: {
		assessmentId: string;
		assessmentStatus: FastshiftDcAssessmentStatus;
		assessedAt: string;
		topRecommendation: {
			id: string;
			title: string;
			description: string | null;
		} | null;
		results: FastshiftAssessmentResult[];
	};
};

export type GetFastshiftDcInstanceDetailsResponse = {
	readonly data: FastshiftDcInstanceDetails;
};

export type FastshiftPortalConsentRequestPayload = CloudSessionTokenCookieType & {
	requestBody: {
		displayedText: string;
	};
};

export type FastshiftPortalConsentSubmitPayload = CloudSessionTokenCookieType & {
	requestBody: {
		displayedText: string;
		locale: string;
	};
};

export type GetFastshiftTopSupportRequestsRequest = CloudSessionTokenCookieType & {
	domainId: string;
	count: number;
	queryParams: OrgQueryParam;
};

export type GetFastshiftTopSupportRequestsResponseData = {
	supportTickets: {
		supportTicketId: string;
		title: string;
		status: FastshiftTicketStatus;
	}[];
	total: number;
};

export type GetFastshiftTopSupportRequestsResponse = {
	readonly data: GetFastshiftTopSupportRequestsResponseData;
};

export type FastshiftRunbookSummary = {
	runbookId: string;
	title: string;
};

export const FASTSHIFT_RUNBOOK_PRODUCT = {
	JIRA: 'JIRA',
	CONFLUENCE: 'CONFLUENCE',
} as const;

export type FastshiftRunbookProduct =
	(typeof FASTSHIFT_RUNBOOK_PRODUCT)[keyof typeof FASTSHIFT_RUNBOOK_PRODUCT];

export const FASTSHIFT_RUNBOOK_STEP_STATUS = {
	DONE: 'DONE',
	NOT_DONE: 'NOT DONE',
} as const;

export type FastshiftRunbookStepStatus =
	(typeof FASTSHIFT_RUNBOOK_STEP_STATUS)[keyof typeof FASTSHIFT_RUNBOOK_STEP_STATUS];

export type FastshiftRunbookStep = {
	stepId: string;
	stepOrder: number;
	title: string;
	phase: string;
	responsible: string;
	status: FastshiftRunbookStepStatus;
	disabled: boolean;
	risk: boolean;
	requiresComment?: boolean;
};

export type FastshiftRunbook = {
	runbookId: string;
	title: string;
	product: FastshiftRunbookProduct;
	createdAt: number;
	modifiedAt: number;
	dueDate?: string;
	steps: FastshiftRunbookStep[];
};

export type GetFastshiftRunbooksBySupportTicketIdRequest = CloudSessionTokenCookieType & {
	supportTicketId: string;
};

export type GetFastshiftRunbooksBySupportTicketIdResponse = {
	readonly success: true;
	readonly data: FastshiftRunbookSummary[];
};

export type GetFastshiftRunbookRequest = CloudSessionTokenCookieType & {
	supportTicketId: string;
	runbookId: string;
};

export type GetFastshiftRunbookResponse = {
	readonly success: true;
	readonly data: FastshiftRunbook;
};

export type UpdateFastshiftRunbookStepStatusRequest = CloudSessionTokenCookieType & {
	supportTicketId: string;
	runbookId: string;
	stepId: string;
	status: FastshiftRunbookStepStatus;
	/**
	 * Stringified ADF document for the newly added note. Required when
	 * marking a step DONE and the step has requiresComment = true.
	 */
	note?: string;
	title: string;
	phase: string;
	responsible: string;
};

export type UpdateFastshiftRunbookStepStatusResponse = {
	readonly success: true;
};

export type UpdateFastshiftRunbookStepRiskRequest = CloudSessionTokenCookieType & {
	supportTicketId: string;
	runbookId: string;
	stepId: string;
	risk: boolean;
};

export type UpdateFastshiftRunbookStepRiskResponse = {
	readonly success: true;
};

export type UpdateFastshiftRunbookStepDisabledRequest = CloudSessionTokenCookieType & {
	supportTicketId: string;
	runbookId: string;
	stepId: string;
	disabled: boolean;
};

export type UpdateFastshiftRunbookStepDisabledResponse = {
	readonly success: true;
};

// expected errors for getCustomerDomain call
export const CustomerDomainExpectedError = {
	NOT_FOUND: 'NOT_FOUND', // 404 status
	NO_ACCESS: 'NO_ACCESS', // 401 status
} as const;

export type CustomerDomainExpectedError =
	(typeof CustomerDomainExpectedError)[keyof typeof CustomerDomainExpectedError];

// expected errors for getCustomerDomainEngagement call
export const CustomerDomainEngagementExpectedError = {
	NO_ENGAGEMENT: 'NO_ENGAGEMENT', // 404 status
	NO_ACCESS: 'NO_ACCESS', // 401 status
	USER_NOT_PROVISIONED: 'USER_NOT_PROVISIONED', // 403 status
} as const;

export type CustomerDomainEngagementExpectedError =
	(typeof CustomerDomainEngagementExpectedError)[keyof typeof CustomerDomainEngagementExpectedError];

export type GetCustomerDomainEngagementResponse = {
	readonly data: GetCustomerDomainEngagementResponseData;
};

export type GetCustomerDomainEngagementResponseData = {
	readonly engagementProjects: {
		readonly projectName: string;
		readonly projectIcon: string;
		readonly projectKey: string;
		readonly projectUrl: string;
		readonly boards: {
			readonly boardName: string;
			readonly boardId: string;
			readonly embeds: {
				readonly url: string;
				readonly type: 'TIMELINE' | 'CALENDAR';
			}[];
		}[];
	}[];
	readonly engagementSpaces: {
		readonly spaceKey: string;
		readonly spaceUrl: string;
	}[];
};

type PartnerStatus = {
	discountTier: null | string;
	programLevelGlobal: null | string;
	externalSystemIdentifier: null | string;
	programLevel: null | string;
};
type TransactionAccountPartition = {
	id: string;
	partitionKey: string;
};
export type CcpTransactionAccountData = {
	id: string;
	version: number;
	transactionAccountPartition: TransactionAccountPartition;
	transactionAccount: TransactionAccountPartition;
	number: string;
	partnerStatus: PartnerStatus;
	createdAt: string;
	updatedAt: string;
	name: null | string;
	description: null | string;
};
export type GetUserBillingAdminTransactionAccounts = {
	data: Array<CcpTransactionAccountData>;
};
export type GetUserBillingAdminTransactionAccountsRequest = {
	cloudSessionTokenCookie: Cookie | undefined;
	makeS2sRequest?: boolean;
	userContext?: string;
};

export type MadlibContentGenerationRequest = {
	readonly spaceName: string;
	readonly workingOnSummary: string;
	readonly goal: string;
	readonly teamType: string;
};

export type MadlibContentGenerationResponse = {
	sampleIssues: {
		epic: MadlibContentGenerationEpic;
		childWorkItems: MadlibContentGenerationChildWorkItem[];
	};
};

export type MadlibContentGenerationEpic = {
	epicName: string;
	description: string;
};

export type MadlibContentGenerationChildWorkItem = {
	summary: string;
};

export type JobContext = Record<string, unknown>;

export const JobName = {
	WAC_SIGNUP: 'wac-signup',
} as const;

export type JobNameType = (typeof JobName)[keyof typeof JobName];

export type ActivateProductCrossflowRequest<T extends JobContext> = {
	cloudId: string;
	productKey: ProductKey;
	jobName: JobNameType;
	context?: T;
};

export type ActivateProductCrossflowResponse = {
	started: boolean;
	reasons?: string[];
};

export type ActivateProductCrossflowPlanResponse = {
	allowed: boolean;
	tasks: ProvisioningTask[];
	reasons?: string[];
	context: Record<string, unknown>;
};

type ProvisioningTask<
	TContext extends Record<string, unknown> = Record<string, unknown>,
	TReason extends string = string,
> = {
	name: string;
	allowed: boolean;
	reason?: TReason;
	context?: TContext;
};

type WaitForActivateProductCrossflowJobContext = Record<string, unknown> & {
	success: boolean;
};

export type WaitForActivateProductCrossflowRequest = {
	cloudId: string;
	productKey: ProductKey;
	jobName: JobNameType;
	context?: WaitForActivateProductCrossflowJobContext;
};

export const ProvisioningStatus = {
	PROVISIONING_COMPLETED: 'PROVISIONING_COMPLETED',
	PROVISIONING_FAILED: 'PROVISIONING_FAILED',
	PROVISIONING_IN_PROGRESS: 'PROVISIONING_IN_PROGRESS',
	PROVISIONING_ABORTED: 'PROVISIONING_ABORTED',
	PROVISIONING_PROCESS_NOT_FOUND: 'PROVISIONING_PROCESS_NOT_FOUND',
	PROVISIONING_FAILED_COMPLETED: 'PROVISIONING_FAILED_COMPLETED',
} as const;
export type ProvisioningStatus = (typeof ProvisioningStatus)[keyof typeof ProvisioningStatus];

export const TaskName = {
	ACTIVATE: 'activate',
	WAIT_FOR_ACTIVATION: 'wait-for-activation',
	WAIT_FOR_JIRA: 'wait-for-jira',
	CREATE_JIRA_PROJECT: 'create-jira-project',
	ONBOARD_JPD_PROJECT: 'onboard-jpd-project',
} as const;

export type WaitForActivateProductCrossflowResponse = {
	step: string;
	status: ProvisioningStatus;
	context?: WaitForActivateProductCrossflowJobContext;
};

export type EmailInviteRequest = {
	email: string;
	projectRoleIds: number[];
	continueUrl: string;
	atlOrigin: string;
};

export type EmailInviteResponse = {
	inviteeUserKey: string;
	inviteeAaId: string;
	inviteStatus: string;
};
